// src/tools/MigrateLibraryFiles.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  doc,
  getDoc,
  collection,
  writeBatch,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '@root/firebase';

export default function MigrateLibraryFiles() {
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState({}); // raw object in files/library
  const [copied, setCopied] = useState(0);
  const [errors, setErrors] = useState([]);
  const [cleanUp, setCleanUp] = useState(false); // toggle to delete old fields after copy
  const [done, setDone] = useState(false);

  // 1) Load source records from files/library (a single doc whose fields are your files)
  const load = async () => {
    setLoading(true);
    setErrors([]);
    setCopied(0);
    setDone(false);
    try {
      const ref = doc(db, 'files', 'library');
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : {};
      setSource(data);
    } catch (e) {
      console.error(e);
      setErrors((prev) => [...prev, String(e)]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entries = useMemo(() => Object.entries(source || {}), [source]);
  const total = entries.length;

  // 2) Migrate: copy to files/library/files/{autoId}
  const migrate = async () => {
    if (!total) return;

    setLoading(true);
    setErrors([]);
    setCopied(0);
    setDone(false);

    try {
      const parentDocRef = doc(db, 'files', 'library');
      const filesColRef = collection(db, 'files', 'library', 'files');

      // Batch in chunks (Firestore limit 500 writes per batch)
      const CHUNK = 300;
      for (let i = 0; i < entries.length; i += CHUNK) {
        const batch = writeBatch(db);
        const slice = entries.slice(i, i + CHUNK);

        slice.forEach(([oldKey, value]) => {
          // create auto-id doc under files/library/files
          // doc(collectionRef) => autoId ref
          const destRef = doc(filesColRef);
          // copy payload; include oldKey for traceability
          batch.set(destRef, {
            ...value,
            oldKey,
            migratedAt: serverTimestamp(),
          });

          // optionally remove the old field from files/library
          if (cleanUp) {
            batch.update(parentDocRef, { [oldKey]: deleteField() });
          }
        });

        await batch.commit();
        setCopied((prev) => prev + slice.length);
      }

      setDone(true);
    } catch (e) {
      console.error(e);
      setErrors((prev) => [...prev, String(e)]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='container py-3'>
      <h3 className='mb-2'>Migrate Library Files</h3>
      <p className='text-muted mb-3'>
        Source: <code>files/library</code> (fields keyed by file name) →
        Destination: <code>files/library/files/&lt;autoId&gt;</code>
      </p>

      <div className='d-flex align-items-center gap-2 mb-3'>
        <button
          className='btn btn-outline-secondary btn-sm'
          onClick={load}
          disabled={loading}
        >
          Reload source
        </button>
        <button
          className='btn btn-primary btn-sm'
          onClick={migrate}
          disabled={loading || total === 0}
        >
          {loading ? 'Migrating…' : 'Migrate to subcollection'}
        </button>

        <label className='ms-3 d-inline-flex align-items-center gap-2'>
          <input
            type='checkbox'
            checked={cleanUp}
            onChange={(e) => setCleanUp(e.target.checked)}
          />
          <span>Delete old fields after copy</span>
        </label>
      </div>

      <div className='mb-2'>
        <strong>Total source records:</strong> {total}
        {copied > 0 && (
          <span className='ms-2'>
            • <strong>Copied:</strong> {copied}
          </span>
        )}
        {done && <span className='badge bg-success ms-2'>Done</span>}
      </div>

      {!!errors.length && (
        <div className='alert alert-danger py-2'>
          <div className='fw-bold mb-1'>Errors</div>
          <ul className='mb-0'>
            {errors.map((e, i) => (
              <li key={i} style={{ whiteSpace: 'pre-wrap' }}>
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 3) Preview source before moving */}
      <div className='card mt-3'>
        <div className='card-header'>
          <strong>Preview Source (files/library)</strong>
        </div>
        <div className='card-body' style={{ maxHeight: 420, overflow: 'auto' }}>
          {total === 0 ? (
            <div className='text-muted'>No records found on files/library.</div>
          ) : (
            <table className='table table-sm'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Old Key (file name)</th>
                  <th>Example fields</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(([k, v], idx) => (
                  <tr key={k}>
                    <td>{idx + 1}</td>
                    <td className='text-break'>{k}</td>
                    <td style={{ maxWidth: 520 }}>
                      <code className='d-block text-break'>
                        {JSON.stringify(v, null, 2)}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
